// Экран «Модель» — шапка и три вида (п.20, 2026-09-07). Последняя часть переезда:
// раньше шапка (виды, галочки столбцов, «Актуализировать», базовый план и дата
// последней актуализации) жила в оболочке, а таблицу/Гант/Сводную она встраивала
// по одному. Теперь экран самодостаточный: шапка здесь, виды — дочерние компоненты.
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ask } from '../dialog';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';
import { Plan, type SvodkaOshibok } from './Plan';
import type { GantKnopki } from '../GantHolst';
import { TipovojEtazh } from './TipovojEtazh';
import { Gruppirovka, priznaki } from '../Gruppirovka';

type Baseline = Data<'/api/projects/{pid}/baselines', 'get'>[number];
type Project = Data<'/api/projects', 'get'>[number];
type Recalc = Data<'/api/projects/{pid}/recalc', 'post'>;
type View = 'table' | 'gant' | 'pivot' | 'shahm';
type Cols = { hand: boolean; res: boolean; pred: boolean; fakt: boolean; sprav: boolean;
              base: boolean; noSum: boolean };

// «Скрыть суммарные» — перечёркнутый глаз всегда, нажатое состояние — подсветка кнопки (2026-09-15)

const COLS_DEF: Cols = { hand: true, res: false, pred: false, fakt: false, sprav: false,
                         base: false, noSum: false };
/** ключи в localStorage те же, что были у оболочки: настройки пользователя не сбрасываются */
function loadCols(): Cols {
  try {
    const raw = JSON.parse(localStorage.getItem('plot_plancols') || '{}') as
      { h?: boolean; r?: boolean; pr?: boolean; f?: boolean; s?: boolean; b?: boolean; ns?: boolean };
    // блок «Сроки» по умолчанию выключен (2026-10-15, шаг 2 по #61): шесть колонок ручных правок,
    // у большинства работ пустые, забирали половину ширины у Ганта; сохранённый выбор — как был
    return { hand: raw.h === undefined ? false : !!raw.h, res: !!raw.r, pred: !!raw.pr, fakt: !!raw.f,
      sprav: false, base: !!raw.b, noSum: !!raw.ns };
  } catch { return COLS_DEF; }
}
function saveCols(c: Cols) {
  try {
    localStorage.setItem('plot_plancols',
      JSON.stringify({ h: c.hand, r: c.res, pr: c.pred, f: c.fakt, s: false, b: c.base, ns: c.noSum }));
  } catch { /* приватный режим */ }
}
function loadView(): View {
  // по умолчанию — Гант (2026-10-17, проверка vizor: «правая половина пустая, ни один вид не выбран»)
  try { return (localStorage.getItem('plot_planview') as View) || 'gant'; } catch { return 'gant'; }
}

export function Model({ ctx, ruch = false, struktura = false }: { ctx: ShellCtx; ruch?: boolean;
  /** экран «Структура работ» (вкладка «Работы», 2026-10-15): режим иерархии включён всегда,
      переключателя нет; в «Планировании» (`plan`) иерархии нет вовсе */
  struktura?: boolean }) {
  // режим ручного ввода (решение владельца 2026-09-08): вместо отдельного экрана —
  // кнопка в шапке модели: строка ввода внизу и только задачи ручных работ
  // с 2026-10-17 кнопки «Ручной» в «Планировании» нет: режим включает пункт меню «Ручное планирование»
  // (проп ruch), прежняя память plot_ruchmode не действует — иначе из режима не выйти
  const ruchMode = ruch;
  const [view, setView] = useState<View>(loadView);
  const [cols, setCols] = useState<Cols>(loadCols);
  const [bl, setBl] = useState<Baseline[]>([]);
  const [blCur, setBlCur] = useState<string>('');
  const [aktual, setAktual] = useState<string | null>(null);
  // «Расчётный» — показать сроки по одной модели, будто рамок нет (2026-09-09)
  const [raschet, setRaschet] = useState(false);
  // «Ошибки» (владелец 2026-09-16: экран «Проверки» непонятен — нужна кнопка прямо в модели):
  // таблица оставляет только задачи с замечаниями; не запоминается, чтобы не прятать задачи
  const [oshibki, setOshibki] = useState(false);
  const [svodkaOshibok, setSvodkaOshibok] = useState<SvodkaOshibok>({ vsego: 0, vidy: [] });
  const [vidOshibki, setVidOshibki] = useState('');
  const chisloOshibok = svodkaOshibok.vsego;
  // выбранный вид пропал (исправили или сменили корпус) — показываем все замечания
  useEffect(() => {
    if (vidOshibki && !svodkaOshibok.vidy.some(v => v.chto === vidOshibki)) setVidOshibki('');
  }, [svodkaOshibok, vidOshibki]);
  // Гант на холсте (#100, владелец 2026-09-18: «переезжаем на новый гант»): значок «Гант»
  // открывает его всем, отдельной кнопки-переключателя больше нет. Аварийный выключатель
  // остался на сервере: GANT_CANVAS=0 в env службы → /api/ping отдаёт gant_canvas: false,
  // и тогда рисуется прежний Гант (полосы в ячейках таблицы).
  const [holstRazreshen, setHolstRazreshen] = useState(false);
  useEffect(() => {
    let zhiv = true;
    void call<Data<'/api/ping', 'get'>>('get', '/api/ping').then(r => {
      if (zhiv && r.ok) setHolstRazreshen(!!r.data.gant_canvas);
    });
    return () => { zhiv = false; };
  }, []);
  const [busy, setBusy] = useState(false);
  // панель «Типовой этаж» снизу (владелец 2026-10-05: типовые связи — прямо в модели)
  const [tipEt, setTipEt] = useState(false);
  // экран «Иерархия» вместо таблицы (владелец 2026-10-06): структура работ любой вложенности
  const [ierarh, setIerarh] = useState(struktura);
  // группировка таблицы (конструктор, владелец 2026-10-17): цепочка уровней; пусто — без группировки
  const [cep, setCepS] = useState<string[]>(() => {
    try { const v = JSON.parse(localStorage.getItem('plan_cep') || 'null'); return Array.isArray(v) ? v : ['etap', 'rabota']; } catch { return ['etap', 'rabota']; }
  });
  // «Уровень» в третьей строке: до какого уровня раскрыть (n — номер в цепочке, длина — до задач)
  const [uroven, setUroven] = useState<{ n: number; t: number } | undefined>(undefined);
  const setCep = (c: string[]) => {
    setCepS(c);
    try { localStorage.setItem('plan_cep', JSON.stringify(c)); } catch { /* приватный режим */ }
    setUroven({ n: Math.max(0, c.length - 1), t: Date.now() });
  };
  const [razbivki, setRazbivki] = useState<{ id: number; name: string; osnovnaya: boolean }[]>([]);
  useEffect(() => {
    let zhiv = true;
    void call<{ id: number; name: string; osnovnaya: boolean }[]>('get', p('/api/projects/{pid}/razbivki', { pid: ctx.pid }))
      .then(r => { if (zhiv && r.ok) setRazbivki(r.data); });
    return () => { zhiv = false; };
  }, [ctx.pid]);
  // своя разбивка из цепочки удалена — уровень выпадает
  const cepOk = cep.filter(k => !k.startsWith('rz:') || razbivki.length === 0 || razbivki.some(r => 'rz:' + r.id === k && !r.osnovnaya));
  // кнопки холста Ганта — в шапке слева от значка Ганта (владелец 2026-10-06)
  const [gantKn, setGantKn] = useState<GantKnopki | null>(null);
  const [token, setToken] = useState(0);
  const [bezStarta, setBezStarta] = useState(false);   // старт проекта не задан — даты условные (2026-10-17)   // перечитать вид после пересчёта
  const can = ctx.canPlan;
  const fail = (m: string) => ctx.onError?.(m);

  // старт проекта переехал на экран «Общее» (владелец 2026-09-15, #96)

  // «↺ ↻» переехали в верхнюю строку всех вкладок — Otmena.tsx (2026-10-17)

  const loadHead = useCallback(async () => {
    const [pr, b] = await Promise.all([
      call<Project[]>('get', '/api/projects'),
      call<Baseline[]>('get', p('/api/projects/{pid}/baselines', { pid: ctx.pid })),
    ]);
    if (pr.ok) {
      const cur = pr.data.find(x => x.id === ctx.pid);
      setAktual(cur?.aktual ?? null);
      // плашка — только если старта нет и даты правда условные (расчёт без старта считает от 01.01.1900);
      // у Ситибэя 3 старта нет, но даты настоящие (от факта) — плашки там нет (проверка vizor, 2026-10-17)
      if (cur?.start) setBezStarta(false);
      else {
        const sv = await call<{ stroki: { nachalo: string | null }[] }>('get', p('/api/projects/{pid}/etapy-svodka', { pid: ctx.pid }));
        const min = sv.ok ? sv.data.stroki.map(x => x.nachalo).filter(Boolean).sort()[0] : null;
        setBezStarta(!min || min < '2000-01-01');
      }
    }
    if (b.ok) {
      setBl(b.data);
      const act = b.data.find(x => x.active);
      setBlCur(act ? String(act.id) : (b.data[0] ? String(b.data[0].id) : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void loadHead(); }, [loadHead]);

  function setViewSaved(v: View) {
    setView(v);
    try { localStorage.setItem('plot_planview', v); } catch { /* приватный режим */ }
  }
  function setCol(k: keyof Cols, v: boolean) {
    const next = { ...cols, [k]: v };
    setCols(next);
    saveCols(next);
  }

  async function recalc() {
    setBusy(true);
    const r = await call<Recalc>('post', p('/api/projects/{pid}/recalc', { pid: ctx.pid }), {});
    setBusy(false);
    if (!r.ok) { fail(r.error.message); return; }
    ctx.onError?.('');
    setAktual(r.data.aktualizirovano);
    const total = (r.data.korpusa || []).reduce((s, k) => s + (k.zadach || 0), 0);
    // круг в задачах (п.34, 2026-09-10): движок вернул −1 — даты посчитаны
    // страховочным способом, планеру надо об этом сказать, а не молчать
    const krug = (r.data.korpusa || []).filter(k => (k.iteraciy ?? 0) < 0).map(k => k.korpus);
    if (krug.length) {
      fail('Задачи ждут друг друга по кругу (' + krug.join(', ') + ') — даты посчитаны'
        + ' страховочным способом. Разбор — на экране «Проверки», строка «Расчёт зациклился».');
    } else {
      ctx.onSaved?.('Пересчёт готов: ' + total + ' задач за ' + r.data.sekund + ' с. Даты обновлены.');
    }
    ctx.onAfterRecalc?.(r.data.aktualizirovano);
    setToken(t => t + 1);
  }


  // кнопка «Связи» (стрелки связей на Ганте, 2026-09-10) убрана владельцем 2026-09-15 (#90)

  // значок «Панель снизу» убран владельцем 2026-09-16: карточка задачи открывается нижней
  // панелью сама при клике по задаче (память plot_detali_vnizu больше не читается)

  /** значение пункта-действия в списке базовых планов */
  const NOVYJ_BAZOVYJ = 'novyj';

  async function setBaseline() {
    const d = new Date(), p2 = (n: number) => String(n).padStart(2, '0');
    const def = 'План от ' + p2(d.getDate()) + '.' + p2(d.getMonth() + 1) + '.' + d.getFullYear();
    const name = await ask('Название новой версии базового плана (снимок текущих расчётных дат):', def);
    // список сразу возвращаем к действующей версии: пункт «＋ Задать…» — действие,
    // а не выбор, и он не должен оставаться выбранным (2026-09-10)
    setBlCur(String(bl.find(b => b.active)?.id ?? ''));
    if (name === null) return;
    const r = await call<{ id: number; zadach: number }>('post',
      p('/api/projects/{pid}/baselines', { pid: ctx.pid }), { name: name.trim() || def });
    if (!r.ok) { fail(r.error.message); return; }
    ctx.onError?.('');
    ctx.onSaved?.('Базовый план «' + (name.trim() || def) + '» задан: ' + r.data.zadach
      + ' задач. Он стал действующим.');
    await loadHead();
    setToken(t => t + 1);
  }

  async function pickBaseline(id: string) {
    setBlCur(id);
    if (!id) return;
    const r = await call('patch', p('/api/projects/{pid}/baselines/{bid}', { pid: ctx.pid, bid: id }),
      { active: true });
    if (!r.ok) { fail(r.error.message); return; }
    ctx.onError?.('');
    setBl(prev => prev.map(b => ({ ...b, active: String(b.id) === id })));
    setToken(t => t + 1);
  }

  // дата последнего пересчёта — коротко, чтобы полоса оставалась в одну строку
  const actShort = (() => {
    if (!aktual) return 'не актуализировалось';
    const d = new Date(aktual), p2 = (n: number) => String(n).padStart(2, '0');
    return p2(d.getDate()) + '.' + p2(d.getMonth() + 1) + ' ' + p2(d.getHours()) + ':' + p2(d.getMinutes());
  })();

  // гнёзда в строке вкладок и в панели фильтров (App, Filters) — появляются раньше экрана
  const [gnezda, setGnezda] = useState<{ l: HTMLElement | null; r: HTMLElement | null; g: HTMLElement | null }>({ l: null, r: null, g: null });
  useEffect(() => {
    setGnezda({ l: document.getElementById('vklL'), r: document.getElementById('vklR'), g: document.getElementById('fslotR') });
  }, [ctx.pid]);
  const recalcKn = (
    <button className={'pv-recalc' + (ctx.datyUstareli && !busy ? ' ustar' : '')} disabled={busy}
      title={busy ? 'Считаю…' : (ctx.datyUstareli ? 'Даты устарели после правок — нажмите, чтобы пересчитать. ' : 'Пересчитать даты всех задач проекта. ')
        + 'Последний раз: ' + actShort}
      onClick={() => void recalc()}>{busy ? 'Считаю…' : 'Пересчитать'}</button>
  );

  // виду передаём тот же контекст плюс выбранные столбцы и свой счётчик обновления
  const childCtx: ShellCtx = {
    ...ctx, planCols: cols, reloadToken: (ctx.reloadToken || 0) * 1000 + token,
    // правка в таблице или Ганте прошла — стрелка «отменить» должна ожить
    onSaved: (m?: string) => ctx.onSaved?.(m),
  };

  return (
    <div>
      {/* «↺ ↻» и «Пересчитать» — слева от вкладок (владелец 2026-10-17; раньше «Пересчитать» был справа) (эскиз «Планирования», владелец 2026-10-17);
          на «Структуре работ» (struktura) шапка прежняя */}
      {can && !struktura && gnezda.l && createPortal(recalcKn, gnezda.l)}
      {/* кнопки Ганта — «Сегодня», «−», «+» — в первой строке справа, над переключателем видов */}
      {view === 'gant' && gantKn && !ierarh && gnezda.g && createPortal(
        <span className="pv-gant-kn">
          <button className="gh-kn" onClick={gantKn.kSegodnya} title="Прокрутить Гант к сегодняшнему дню">Сегодня</button>
          <button className="gh-kn" onClick={() => gantKn.masshtab(1 / 1.5)} title="Мельче (Ctrl + колесо над Гантом)">−</button>
          <button className="gh-kn" onClick={() => gantKn.masshtab(1.5)} title="Крупнее (Ctrl + колесо над Гантом)">+</button>
        </span>, gnezda.g)}
      {bezStarta && !struktura && <div className="pv-bez-starta">Старт проекта не задан — даты условные (считаются от 01.01.1900), поэтому Гант не строится.
        Задайте старт в «⚙ Настройки проекта» рядом со списком проектов слева и нажмите «Пересчитать».</div>}
      <div className="bar plan-bar actbar pv-shapka">
        {(struktura || !gnezda.l) && can && !struktura && recalcKn}
        {struktura && <span className="pv-stroim"><span className="pv-stroim-b on" style={{ cursor: 'default' }}
          title="Структура работ проекта: этапы любой вложенности и работы под ними, как структура в Project. Работы из неё сразу попадают в справочник и график"><IkIerarh /> Структура работ</span></span>}
        {!ierarh && <>
        {/* вторая строка: слева — что и как в таблице, справа — что показать правее таблицы */}
        <div className="pv-r2">
          {/* «Типовое | Ручное» (#125, владелец 2026-10-27): раскатанный график или задачи, набранные руками;
              пункт меню «Ручное планирование» открывает тот же режим */}
          {!struktura && <span className="rb-seg" title="Типовое — работы, раскатанные по этажам и захваткам; ручное — задачи, набранные руками (котлован, подготовка, сети)">
            <button className={ruchMode ? '' : 'on'} onClick={() => ruchMode && ctx.onGoTab?.('plan')}>Типовое</button>
            <button className={ruchMode ? 'on' : ''} onClick={() => !ruchMode && ctx.onGoTab?.('ruch')}>Ручное</button>
          </span>}
          {/* в ручном режиме таблица — плоский список, группировка не действует; но остаётся на месте
              бледной — шапка при переключении «Типовое | Ручное» не прыгает (владелец 2026-10-27) */}
          <span className={ruchMode ? 'pv-nedeyst' : 'pv-obertka'}
            title={ruchMode ? 'В ручном планировании группировка не действует — задачи идут списком' : undefined}>
            <Gruppirovka ctx={ctx} razbivki={razbivki} cep={cepOk} onCep={setCep} /></span>
          <span className="pv-vidy pv-stolbcy" title="Какие группы столбцов показать в таблице">
            <span className="muted pv-stolbcy-zag">Столбцы</span>
            {([['hand', 'Сроки', 'Сроки: ручные длительность, сдвиг начала и даты — правка расчёта'],
               ['res', 'Ресурс', 'Ресурс: объём работ и ручной приоритет задачи в очереди бригады'],
               ['pred', 'Предш.', 'Предшественники и даты-источники расчёта: по связи, по ресурсу (бригаде)'],
               ['fakt', 'Факт', 'Факт: подрядчик и фактические начало и окончание (из шахматки факта)'],
               ['base', 'Базовый', 'Базовый план: начало и окончание из выбранной версии']] as [keyof Cols, string, string][])
              .map(([k, t, ttl]) => (
                <button key={k} className={'sec pv-btn' + (cols[k] ? ' on' : '')}
                  title={ttl + '. Клик — показать или скрыть столбцы; Гант и сводная встают правее (#47)'}
                  onClick={() => setCol(k, !cols[k])}>{t}</button>
              ))}
          </span>
          <button className={'sec pv-btn pv-osh' + (oshibki ? ' on' : '')}
            style={!oshibki && chisloOshibok ? { color: 'var(--plot-danger)' } : undefined}
            title={oshibki
              ? 'Вернуть в таблицу все задачи'
              : chisloOshibok
                ? 'Показать только задачи с замечаниями (' + chisloOshibok + ' в выбранных корпусах): ошибки — '
                  + 'розовые, конфликты — песочные; что не так — в подсказке строки'
                : 'В выбранных корпусах задач с замечаниями нет'}
            onClick={() => setOshibki(!oshibki)}>Ошибки{chisloOshibok ? ' ' + chisloOshibok : ''}</button>
          {/* виды — словами (эскиз): таблица есть всегда, повторный щелчок убирает вид справа */}
          {!struktura && <span className="pv-vidy pv-vidy-sl" title="Что показать справа от таблицы">
            {([['gant', 'Гант', 'Диаграмма Ганта: полосы задач по календарю. Тянутся полосы: суммарная целиком — группа начнётся не раньше новой даты, за правый край — длительности; полоса задачи — ручные сроки (отмена — стрелкой «назад»); двойной клик по суммарной — сбросить корректировки'],
               ['pivot', 'Сводная', 'Сводная по месяцам: сколько задач заканчивается'],
               ['shahm', 'Шахматка', 'Поэтажная шахматка: по корпусам этажи × работы, в клетке — дата окончания. Цвет: закончено, начато, срок прошёл']] as [View, string, string][])
              .map(([v, t, ttl]) => (
                <button key={v} className={'sec pv-btn' + (view === v ? ' on' : '')} title={(view === v ? 'Убрать. ' : '') + ttl}
                  onClick={() => setViewSaved(view === v ? 'table' : v)}>{t}</button>
              ))}
          </span>}
        </div>
        {/* третья строка: настройки выбранного во второй (эскиз) — версия базового плана, вид замечаний,
            под Гантом — связи и расчётный график. Высота постоянная, таблица не прыгает */}
        <div className="pv-r3">
          {cepOk.length > 0 && <span className={'pv-r3-g' + (ruchMode ? ' pv-nedeyst' : '')}
            title={ruchMode ? 'В ручном планировании уровни не действуют' : undefined}><span className="muted">Уровень</span>
            <span className="pv-vidy">
              {[...cepOk.map((k, i) => [i, priznaki(razbivki).find(x => x[0] === k)?.[2] || k] as [number, string]), [cepOk.length, 'Задачи'] as [number, string]]
                .map(([n, t]) => <button key={n} className={'sec pv-btn' + (uroven?.n === n ? ' on' : '')}
                  title={n === cepOk.length ? 'Раскрыть всё до задач' : 'Показать до уровня «' + t + '», глубже — свёрнуто'}
                  onClick={() => setUroven({ n, t: Date.now() })}>{t}</button>)}
            </span></span>}
          {cols.base && <label className="pv-r3-g"><span className="muted">Базовый план</span>
            <select value={blCur} style={{ maxWidth: 240 }} disabled={!bl.length && !can}
              title="Какая версия базового плана считается действующей; там же — задать новую"
              onChange={e => {
                if (e.target.value === NOVYJ_BAZOVYJ) { void setBaseline(); return; }
                void pickBaseline(e.target.value);
              }}>
              {!bl.length && <option value="">— не задан —</option>}
              {bl.map(b => <option key={b.id} value={b.id}>{b.name + ' (' + b.sozdan.slice(0, 10) + ')'}</option>)}
              {can && <option value={NOVYJ_BAZOVYJ}>＋ Задать базовый план…</option>}
            </select></label>}
          {oshibki && svodkaOshibok.vidy.length > 0 && <label className="pv-r3-g"><span className="muted">Вид замечания</span>
            <select value={vidOshibki} style={{ width: 240 }}
              title="Какой вид замечаний показать. Ошибка — данные неверны, конфликт — сроки противоречат друг другу"
              onChange={e => setVidOshibki(e.target.value)}>
              <option value="">Все замечания — {chisloOshibok}</option>
              {svodkaOshibok.vidy.map(v => (
                <option key={v.chto} value={v.chto}>{(v.gruppa === 'ошибка' ? '● ' : '○ ') + v.chto + ' — ' + v.n}</option>
              ))}
            </select></label>}
          {view === 'gant' && <span className="pv-r3-prav">
            {gantKn && <button className={'sec pv-btn' + (gantKn.vseSvyazi ? ' on' : '')} onClick={gantKn.pereklSvyazi}
              title={gantKn.vseSvyazi ? 'Показывать связи только у полосы под мышью'
                : 'Показать все связи: сплошные — по справочнику, зелёные — ручные предшественники, пунктир — цепочка бригады'}>Связи</button>}
            <button className={'sec pv-btn' + (raschet ? ' on' : '')}
              title={raschet
                ? 'Вернуться к плану: даты с учётом рамок суммарных задач'
                : 'Расчётный график: сроки по одной модели — связи, очередь бригад, ручные даты '
                  + 'и факт — без декоративных сроков (рамок и декоративных длительностей от корректировки '
                  + 'суммарной). Только показ, ничего не меняется'}
              onClick={() => setRaschet(!raschet)}>Расчётный</button>
          </span>}
        </div>
        </>}
      </div>

      {/* один экран: слева всегда те же колонки, справа — выбранная визуализация
          (решение владельца 2026-09-08) */}
      <Plan ctx={childCtx} cep={cepOk} uroven={uroven} ierarh={ierarh} onIerarh={() => setIerarh(true)} onGant={setGantKn} vid={struktura ? 'table' : view} ruch={ruchMode} summ={view === 'gant'} raschet={raschet}
        oshibki={oshibki} vidOshibki={vidOshibki} onOshibki={setSvodkaOshibok}
        holst={holstRazreshen} />
      {tipEt && <TipovojEtazh ctx={childCtx} busy={busy} onRecalc={() => void recalc()} onClose={() => setTipEt(false)} />}
    </div>
  );
}

/** значки группы «построение модели» (2026-10-06) */
function IkIerarh() {
  return (<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <rect x="1" y="1" width="12" height="2.4" rx="1" fill="currentColor" />
    <rect x="4" y="5.8" width="9" height="2.4" rx="1" fill="currentColor" />
    <rect x="7" y="10.6" width="6" height="2.4" rx="1" fill="currentColor" /></svg>);
}
export function IkTipEt() {   // значок панели «Связи»; кнопка убрана 2026-10-07, значок оставлен
  return (<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <rect x="1" y="2" width="5" height="3" rx="1" fill="currentColor" />
    <rect x="8" y="2" width="5" height="3" rx="1" fill="currentColor" opacity=".55" />
    <rect x="1" y="9" width="5" height="3" rx="1" fill="currentColor" opacity=".55" />
    <rect x="8" y="9" width="5" height="3" rx="1" fill="currentColor" />
    <path d="M6 3.5h2M3.5 5v4M10.5 5v4" stroke="currentColor" strokeWidth="1.2" fill="none" /></svg>);
}
